// "Your songs": the tracks this device has counted, from the device library. A song is a name, a state and, when it is kept, a copy
// of the file in OPFS (copies.ts). It is stored in the `meta` store as `track:<id>`. The event log is only touched by `forgetSong`,
// and only after the dancer confirmed it.

import { removeCopy } from '../copies.js';
import { deleteTrackData } from '../engine/log.js';
import { META, open, request } from '../store.js';

export interface SongRecord {
  readonly key: string;
  readonly id: string;
  readonly name: string;
  readonly duration: number;
  /** Taps in force when it was last open: two or more is "Counted". */
  readonly taps: number;
  /** S9e: the song had a count when it was last open (two or more taps, or an automatic proposal in force). Absent in older records: they are "Counted" by `taps`. */
  readonly counted?: boolean;
  readonly openedAt: number;
  /** A copy of the file is on this device (OPFS), so one tap reopens it. */
  readonly kept?: boolean;
  /** Size of the kept copy, bytes. */
  readonly bytes?: number;
  /** The file's media type, for the copy. */
  readonly type?: string;
}

const PREFIX = 'track:';

/** Add or update a song. Fields not given (the kept copy's size) are kept from what is stored. */
export async function saveSong(song: Omit<SongRecord, 'key'>): Promise<void> {
  const db = await open();
  try {
    const store = db.transaction(META, 'readwrite').objectStore(META);
    const before = await request<SongRecord | undefined>(store.get(PREFIX + song.id));
    await request(store.put({ ...(before ?? {}), ...song, key: PREFIX + song.id }));
  } finally {
    db.close();
  }
}

/** Record that a copy of the song is (or is no longer) kept. Creates the entry when the library writer has not yet. */
export async function markKept(song: { id: string; name: string; duration: number }, kept: boolean, bytes = 0, type = ''): Promise<void> {
  const db = await open();
  try {
    const store = db.transaction(META, 'readwrite').objectStore(META);
    const before = await request<SongRecord | undefined>(store.get(PREFIX + song.id));
    const base = before ?? { ...song, taps: 0, openedAt: Date.now() };
    await request(store.put({ ...base, key: PREFIX + song.id, kept, bytes: kept ? bytes : 0, ...(kept && type ? { type } : {}) }));
  } finally {
    db.close();
  }
}

/** Most recently opened first. An empty list when storage is refused. */
export async function listSongs(): Promise<SongRecord[]> {
  try {
    const db = await open();
    try {
      const rows = await request<SongRecord[]>(db.transaction(META, 'readonly').objectStore(META).getAll());
      return rows.filter((r) => typeof r.key === 'string' && r.key.startsWith(PREFIX)).sort((a, b) => b.openedAt - a.openedAt);
    } finally {
      db.close();
    }
  } catch {
    return [];
  }
}

/** Space used by the kept copies, bytes. */
export const spaceUsed = (songs: readonly SongRecord[]): number => songs.reduce((n, s) => n + (s.kept ? s.bytes ?? 0 : 0), 0);

async function deleteRecord(id: string): Promise<void> {
  const db = await open();
  try {
    await request(db.transaction(META, 'readwrite').objectStore(META).delete(PREFIX + id));
  } finally {
    db.close();
  }
}

/** Delete the kept copy and take the song off the list. The taps stay on the device (open the file again and they come back). */
export async function removeSong(id: string): Promise<boolean> {
  const gone = await removeCopy(id);
  if (gone) await deleteRecord(id);
  return gone;
}

/** After a confirm: delete the song's saved taps and fixes (and its truth taps). Returns how many of each. */
export async function forgetSong(id: string): Promise<{ events: number; truth: number }> {
  return deleteTrackData(id);
}
